<template>
    <div class="sim-plan-display">

        <section class="plan-shell" aria-labelledby="sim-free-plan-heading">
            <h2 id="sim-free-plan-heading" class="plan-shell__heading">
                {{ $t('update_plan.sub_title') }}
            </h2>
            <div class="plan-shell__panel">
                <div class="plan-row" v-if="deviceInfo">
                    <span class="plan-row__label">{{ $t('my_sim.uid') }}:</span>
                    <span class="plan-row__value">{{ deviceInfo.uid }}</span>
                </div>
                <div class="plan-row" v-else-if="lockDeviceData">
                    <span class="plan-row__label">{{ $t('my_sim.uid') }}:</span>
                    <span class="plan-row__value">{{ lockDeviceData.uid }}</span>
                </div>
                <div class="plan-row" v-else>
                    <span class="plan-row__label">{{ $t('cloud.common.simCard') }}</span>
                    <span class="plan-row__value">{{ simInfo?.simCardCode }}</span>
                </div>
                <div class="plan-row">
                    <span class="plan-row__label">{{ $t('update_plan.plan_label') }}:</span>
                    <span class="plan-row__value">{{ $options.filters?.planName(currentSubscription?.planInfo) }}</span>
                </div>
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import {
    ESeriesCategory,
    ICancelSubscriptionPreview,
    ICloudService,
    ITotalDevice
} from '@reolink-cloud/interface';
import Vue, { PropType } from 'vue';
import { calcFinalDataStr, desensitizeString } from '@/common/js/utils';
import { SimManager } from '@/lib/simManager';
import { getDeviceTitle } from '@/utils/business';
import { deviceManager } from 'VIEW/checkout/lib/deviceManager';

export default Vue.extend({
    props: {
        simInfo: {
            type: Object as PropType<SimManager|null>
        },
        subId: {
            type: String
        },
        refundData: {
            type: Array as PropType<ICancelSubscriptionPreview['refund']>
        },
        deviceInfo: {
            type: Object as PropType<ITotalDevice | null>,
            default: null
        },
    },
    data: function() {

        return {
            desensitized: false
        };

    },
    computed: {
        currentSubscription(): ICloudService|undefined {

            return this.simInfo?.currentSubscription;

        },
        isAddon(): boolean {

            return this.currentSubscription?.planInfo?.series?.category === ESeriesCategory.CLOUD_DATA_ADD_ON_PLAN;

        },
        isCloudPlan(): boolean {

            return this.currentSubscription?.planInfo?.series?.category === ESeriesCategory.CLOUD_STORAGE_PLAN;

        },
        addOnString(): string {

            return this.simInfo?.activeAddOns.map( instance => {

                return `${calcFinalDataStr( instance.dataSize, true, 0 )} ${instance.planInfo?.series?.i18n.name ?? ''}`;

            }).join( ', ' ) ?? '';

        },
        simString(): string {

            return this.$t( 'cart.simInfo', {
                code: this.desensitized ? desensitizeString( this.simInfo?.data.simCard?.id ) : this.simInfo?.data.simCard?.id,
                iccid: this.desensitized ? desensitizeString( this.simInfo?.iccid ) : this.simInfo?.iccid,
            }).toString();

        },
        lockDeviceData(): ITotalDevice | undefined {

            return this.simInfo?.lockedDevice;

        },
        lockedDeviceTitle(): string {

            return this.lockDeviceData ? getDeviceTitle( this.lockDeviceData ) : '';

        },
        lockDeviceString(): string {

            if ( !this.lockDeviceData ) {

                return '';

            }
            return this.$t( 'cart.deviceInfo', {
                model: this.lockDeviceData.model,
                uid: this.handleDesensitizedString( this.lockDeviceData.uid ),
                title: this.handleDesensitizedString( this.lockedDeviceTitle )
            }).toString();

        }
    },
    async created() {

        await deviceManager.init();

    },
    methods: {
        handleDesensitizedString( str: string ): string {

            if ( this.desensitized ) {

                return desensitizeString( str );

            }
            return str;

        }
    }
});
</script>

<style lang="less" scoped>
.sim-plan-display{
    .free-sim-plan-complete {
        background-color: @bg_01;
        color: @font_primary;
    }

    .main-column {
        box-sizing: border-box;
        width: 100%;
        max-width: 1000px;
        box-sizing: border-box;
        margin: 0 auto;
        padding: 60px 0px 80px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 60px;
        @media @snapeak_mobile {
            padding: 48px 20px 64px;
            gap: 40px;
        }
    }

    /* 135:18303：标题 + 副文案，列 gap 4px，副文案 #777 16 Medium */
    .hero {
        width: 100%;
        max-width: 710px;
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 4px;
    }

    .hero__title {
        margin: 0;
        font-family: @font_01;
        font-size: 32px;
        font-weight: 600;
        color: @font_primary;
        .mobile({
            font-size: 30px
        });
    }

    .hero__lead {
        margin: 0;
        font-family: @font_01;
        font-size: 16px;
        font-weight: 500;
        color: @font_secondary;
        .mobile({
            font-size: 14px;
        });
    }

    /* 方案卡 + 联系文案 + 按钮：DSL 内层区块相对间距 24px */
    .lower-block {
        width: 100%;
        max-width: 1000px;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 24px;
    }

    /* Step1 外层：bg_02、圆角 12、内边距 32、内部纵向 gap 16 */
    .plan-shell {
        width: 100%;
        background-color: @bg_02;
        border-radius: 12px;
        padding: 32px;
        box-sizing: border-box;
        display: flex;
        flex-direction: column;
        align-items: left;
        gap: 16px;
        @media @snapeak_mobile {
            padding: 16px;
        }
    }

    .plan-shell__heading {
        margin: 0;
        font-family: @font_01;
        font-size: 20px;
        font-weight: 600;
        color: @font_primary;
        text-align: left;
        .mobile({
            font-size: 16px;
        });
    }

    /* 内层面板：bg_01、圆角 8、padding 16 24、行 gap 12 */
    .plan-shell__panel {
        width: 100%;
        box-sizing: border-box;
        background-color: @bg_01;
        border-radius: 8px;
        padding: 16px 24px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        .mobile({
            padding: 12px;
        });
    }

    .plan-row {
        display: flex;
        flex-direction: row;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 12px;
        font-family: @font_01;
        font-size: 16px;
        .mobile({
            flex-direction: column;
            font-size: 14px;
            line-height: 16px;
            gap: 4px;
        });
    }

    .plan-row__label {
        color: @font_secondary;
        font-weight: 400;
    }

    .plan-row__value {
        color: @font_primary;
        font-weight: 500;
    }

    .plan-row__value--price {
        color: #FF8836;
        font-weight: 500;
    }

    .plan-row__extra {
        margin: 0;
        font-size: 14px;
        line-height: 20px;
        color: @font_secondary;
    }

    .contact-block {
        margin: 0;
        width: 100%;
        max-width: 710px;
        text-align: center;
        font-family: @font_01;
        font-size: 16px;
        font-weight: 500;
        color: @font_secondary;
    }

}
</style>
